import type { ReactNode } from 'react';

import {
  Navigate,
  Routes,
  Route,
} from 'react-router-dom';

import { Header } from './components/Header';
import { Footer } from './components/Footer';

import { HomePage } from './pages/HomePage';
import { QuickClientCreatePage } from './pages/QuickClientCreatePage';
import { PiercingBookingPage } from './pages/PiercingBookingPage';
import { OnlineBookingPage } from './pages/OnlineBookingPage';

import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import BecomePartnerPage from './pages/BecomePartnerPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

import AccountDashboard from './pages/AccountDashboard';
import ProfilePage from './pages/ProfilePage';
import AppointmentsPage from './pages/AppointmentsPage';
import DocumentsPage from './pages/DocumentsPage';
import DocumentViewerPage from './pages/DocumentViewerPage';
import SecurityPage from './pages/SecurityPage';
import PreferencesPage from './pages/PreferencesPage';
import NotificationsPage from './pages/NotificationsPage';
import DataExportPage from './pages/DataExportPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import DeleteAccountPage from './pages/DeleteAccountPage';

import ProtectedRoute from './components/ProtectedRoute';
import SessionExpiredModal from './components/SessionExpiredModal';

import AppointmentDetailPage from './pages/AppointmentDetailPage';
import AccountAppointmentDetailPage from './pages/AccountAppointmentDetailPage';

import VerifyEmailPage from './pages/VerifyEmailPage';

import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelledPage from './pages/PaymentCancelledPage';

import AppointmentCancelPage from './pages/AppointmentCancelPage';

import { LegalInfoPage } from './pages/LegalInfoPage';
import { RefundPolicyPage } from './pages/RefundPolicyPage';

import { TestModeBanner } from './components/TestModeBanner';

import { HomePageV2 } from './pages/HomePageV2';

import { BoutiquePage } from './pages/BoutiquePage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';

import { CartProvider } from './context/CartContext';

import {
  useFrontoffWorkingMode,
  useSystemStatus,
} from './hooks/useSystemStatus';

/*
 * =========================================================
 * FULL MODE ROUTE
 * =========================================================
 *
 * Cette protection interdit complètement l'accès
 * aux routes concernées lorsque le front-office
 * fonctionne en mode limité.
 *
 * Ce n'est donc pas uniquement un masquage de menu :
 *
 * /account/profile
 * /account/appointments
 * etc.
 *
 * sont réellement bloqués côté router.
 */
function FullModeRoute({
  children,
}: {
  children: ReactNode;
}) {
  const {
    isLimited,
    loading,
  } = useFrontoffWorkingMode();

  /*
   * Tant que le mode n'est pas déterminé,
   * on ne rend surtout pas la page protégée.
   *
   * Cela évite un bref affichage d'une page
   * interdite avant la redirection.
   */
  if (loading) {
    return null;
  }

  if (isLimited) {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  return children;
}

/*
 * =========================================================
 * ACCOUNT FULL MODE ROUTE
 * =========================================================
 *
 * Même logique que FullModeRoute, mais ce composant
 * rend plus lisibles les routes du compte.
 *
 * En mode limité, toutes les pages du compte utilisant
 * ce wrapper deviennent inaccessibles même si
 * l'utilisateur connaît directement leur URL.
 */
function FullModeAccountRoute({
  children,
}: {
  children: ReactNode;
}) {
  const {
    isLimited,
    loading,
  } = useFrontoffWorkingMode();

  if (loading) {
    return null;
  }

  if (isLimited) {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  return (
    <ProtectedRoute>
      {children}
    </ProtectedRoute>
  );
}

/*
 * =========================================================
 * TATTOO BOOKING
 * =========================================================
 */

function TattooBookingRoute() {
  const {
    tattooBookingEnabled,
    loading,
  } = useSystemStatus();

  if (loading) {
    return null;
  }

  return tattooBookingEnabled ? (
    <OnlineBookingPage />
  ) : (
    <Navigate
      to="/"
      replace
    />
  );
}

/*
 * =========================================================
 * APP
 * =========================================================
 */

function App() {
  return (
    <CartProvider>
      <div className="min-h-screen bg-background">
        <TestModeBanner />

        <Header />

        <SessionExpiredModal />

        <main>
          <Routes>
            {/*
             * =================================================
             * PUBLIC ROUTES
             * =================================================
             */}

            <Route
              path="/"
              element={
                <HomePage />
              }
            />

            <Route
              path="/homepage-v2"
              element={
                <FullModeRoute>
                  <HomePageV2 />
                </FullModeRoute>
              }
            />

            {/*
             * AUTH
             *
             * Ces routes doivent rester disponibles
             * en mode limité afin de permettre :
             *
             * - connexion
             * - création du compte
             * - récupération du mot de passe
             * - vérification email
             */}

            <Route
              path="/login"
              element={
                <LoginPage />
              }
            />

            <Route
              path="/register"
              element={
                <RegisterPage />
              }
            />

            <Route
              path="/forgot-password"
              element={
                <ForgotPasswordPage />
              }
            />

            <Route
              path="/reset-password/:token"
              element={
                <ResetPasswordPage />
              }
            />

            <Route
              path="/verify-email"
              element={
                <VerifyEmailPage />
              }
            />

            {/*
             * FULL MODE ONLY
             */}

            <Route
              path="/become-partner"
              element={
                <FullModeRoute>
                  <BecomePartnerPage />
                </FullModeRoute>
              }
            />

            <Route
              path="/quick-client-create"
              element={
                <FullModeRoute>
                  <QuickClientCreatePage />
                </FullModeRoute>
              }
            />

            {/*
             * =================================================
             * BOOKING
             * =================================================
             */}

            <Route
              path="/book-piercing"
              element={
                <PiercingBookingPage />
              }
            />

            <Route
              path="/book-tattoo"
              element={
                <TattooBookingRoute />
              }
            />

            {/*
             * =================================================
             * LEGAL
             * =================================================
             */}

            <Route
              path="/legal/:page"
              element={
                <LegalInfoPage />
              }
            />

            <Route
              path="/politique-de-remboursement"
              element={
                <RefundPolicyPage />
              }
            />

            {/*
             * =================================================
             * PAYMENT
             * =================================================
             */}

            <Route
              path="/booking/payment-success"
              element={
                <PaymentSuccessPage />
              }
            />

            <Route
              path="/booking/payment-cancelled"
              element={
                <PaymentCancelledPage />
              }
            />

            {/*
             * La suppression / annulation d'un rendez-vous
             * via token doit rester disponible.
             */}
            <Route
              path="/appointments/cancel/:token"
              element={
                <AppointmentCancelPage />
              }
            />

            {/*
             * =================================================
             * APPOINTMENT QR DETAIL
             * =================================================
             *
             * Je le laisse accessible en mode limité
             * lorsqu'on possède le lien QR et qu'on est
             * authentifié.
             *
             * Si tu veux également interdire cette route
             * en limited, on peut la passer sous
             * FullModeAccountRoute.
             */}

            <Route
              path="/appointment/:id"
              element={
                <ProtectedRoute>
                  <AppointmentDetailPage />
                </ProtectedRoute>
              }
            />

            {/*
             * =================================================
             * ACCOUNT ROUTES — FULL MODE ONLY
             * =================================================
             *
             * Toutes ces URLs deviennent réellement
             * inaccessibles en mode limité.
             *
             * Même si l'utilisateur tape manuellement :
             *
             * /account
             * /account/profile
             * /account/appointments
             * /account/security
             * etc.
             *
             * il sera renvoyé vers "/".
             */}

            <Route
              path="/account"
              element={
                <FullModeAccountRoute>
                  <AccountDashboard />
                </FullModeAccountRoute>
              }
            />

            <Route
              path="/account/profile"
              element={
                <FullModeAccountRoute>
                  <ProfilePage />
                </FullModeAccountRoute>
              }
            />

            <Route
              path="/account/appointments"
              element={
                <FullModeAccountRoute>
                  <AppointmentsPage />
                </FullModeAccountRoute>
              }
            />

            <Route
              path="/account/appointments/:id"
              element={
                <FullModeAccountRoute>
                  <AccountAppointmentDetailPage />
                </FullModeAccountRoute>
              }
            />

            <Route
              path="/account/documents"
              element={
                <FullModeAccountRoute>
                  <DocumentsPage />
                </FullModeAccountRoute>
              }
            />

            <Route
              path="/account/documents/view"
              element={
                <FullModeAccountRoute>
                  <DocumentViewerPage />
                </FullModeAccountRoute>
              }
            />

            <Route
              path="/account/security"
              element={
                <FullModeAccountRoute>
                  <SecurityPage />
                </FullModeAccountRoute>
              }
            />

            <Route
              path="/account/preferences"
              element={
                <FullModeAccountRoute>
                  <PreferencesPage />
                </FullModeAccountRoute>
              }
            />

            <Route
              path="/account/notifications"
              element={
                <FullModeAccountRoute>
                  <NotificationsPage />
                </FullModeAccountRoute>
              }
            />

            <Route
              path="/account/export"
              element={
                <FullModeAccountRoute>
                  <DataExportPage />
                </FullModeAccountRoute>
              }
            />

            <Route
              path="/account/verify-email"
              element={
                <FullModeAccountRoute>
                  <EmailVerificationPage />
                </FullModeAccountRoute>
              }
            />

            {/*
             * Backwards compatibility.
             */}
            <Route
              path="/account/email-verification"
              element={
                <FullModeAccountRoute>
                  <EmailVerificationPage />
                </FullModeAccountRoute>
              }
            />

            {/*
             * =================================================
             * DELETE ACCOUNT
             * =================================================
             *
             * EXCEPTION :
             *
             * cette route reste volontairement accessible
             * en mode limité.
             *
             * C'est la seule page /account autorisée
             * dans ce mode.
             */}

            <Route
              path="/account/delete"
              element={
                <ProtectedRoute>
                  <DeleteAccountPage />
                </ProtectedRoute>
              }
            />

            {/*
             * =================================================
             * SHOP — FULL MODE ONLY
             * =================================================
             */}

            <Route
              path="/boutique"
              element={
                <FullModeRoute>
                  <BoutiquePage />
                </FullModeRoute>
              }
            />

            <Route
              path="/boutique/:id"
              element={
                <FullModeRoute>
                  <ProductDetailPage />
                </FullModeRoute>
              }
            />

            <Route
              path="/panier"
              element={
                <FullModeRoute>
                  <CartPage />
                </FullModeRoute>
              }
            />

            <Route
              path="/checkout"
              element={
                <FullModeRoute>
                  <CheckoutPage />
                </FullModeRoute>
              }
            />

            {/*
             * =================================================
             * UNKNOWN ROUTE
             * =================================================
             *
             * Évite qu'une URL inexistante produise
             * une page vide.
             */}

            <Route
              path="*"
              element={
                <Navigate
                  to="/"
                  replace
                />
              }
            />
          </Routes>
        </main>

        <Footer />
      </div>
    </CartProvider>
  );
}

export default App;